// ════════════════════════════════════════════════════════════
//  BattleScene.jsx — the Canvas, the clock, and the event pump
// ════════════════════════════════════════════════════════════
//  ONE place steps the battle: the frame loop below, so the fight
//  and the picture can never disagree about what time it is.
//  The engine's events are read here (by sequence number, never by
//  index) and turned into sparks, scars, words and camera moves.
//
//  ⚑ This component is React.memo'd and its props never change
//  during a fight (refs only). The HUD repaints at 10 Hz in its
//  sibling; if this re-rendered with it, the whole 3D tree would
//  reconcile ten times a second (ie-realtime-perf, Cause 1b).
// ════════════════════════════════════════════════════════════
import { memo, useRef, useMemo } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import * as THREE from "three";
import Arena, { Lights } from "./Arena.jsx";
import Stands from "./Stands.jsx";
import SkyBox from "./SkyBox.jsx";
import { WORLD_BOUNDS, CROWD_IN_FIGHT, FIGHT_CHEER } from "../data/ringside.js";
import Fighter from "./Fighter.jsx";
import Effects from "./Effects.jsx";
import ArtFx from "./ArtFx.jsx";
import { artLook, castLook, strikeLook, impactLook } from "./artSignatures.js";
import BattleCamera from "./BattleCamera.jsx";
import { step, ARENA_R } from "../sim/battle.js";
import { issue } from "../sim/issue.js";
import { STATUS, ARTS } from "../data/arts.js";
import { TIER_LABEL } from "../data/roster.js";

function Clock({ battle, fx, paused, cue, director }) {
  const cursor = useRef(0);
  useFrame((_, dt) => {
    const b = battle.current; if (!b) return;
    // the stands learn who is fighting once (names + the three beings each) — the signs read it
    if (!director.current.names && b.sides && b.sides[0] && b.sides[1]) {
      director.current = { names: { A: b.sides[0].name, B: b.sides[1].name }, teams: b.sides.map((sd) => sd.roster.map((r) => r.line)) };
    }
    // online: the lockstep driver owns time (fixed ticks off the server clock)
    if (b.driver) b.driver.frame();
    else if (!paused.current) step(b, Math.min(dt, 0.05));
    fx.timeScale = paused.current ? 0 : b.timeScale;
    // the pump
    for (const e of b.events) {
      if (e.seq <= cursor.current) continue;
      cursor.current = e.seq;
      // a backlog (a paused tab, a slow first frame) is skipped, not
      // replayed all at once — stale sparks would only bury the screen
      if (b.t - e.t > 0.6 && e.type !== "scorch") continue;
      pump(e, b, fx);
      crowdReact(e, cue.current);
    }
    // the roar: murmuring at base, held up by the big moments, easing back down
    const c = cue.current;
    c.roar = Math.max(0, (c.roar || 0) - dt);
    c.cheer = FIGHT_CHEER.base + (1 - FIGHT_CHEER.base) * Math.min(1, c.roar);
  }, -1);   // before every visual system, so they draw this frame's truth
  return null;
}

// what the fight does to the crowd (dials: FIGHT_CHEER). A ruin sets off THE WAVE
// through the seam the stands expose: cue.waveReq.
function crowdReact(e, c) {
  const hold = (s) => { c.roar = Math.max(c.roar || 0, s); };
  switch (e.type) {
    case "ruined": hold(FIGHT_CHEER.ruined); c.waveReq = (c.waveReq || 0) + 1; break;
    case "transcend": hold(FIGHT_CHEER.transcend); break;
    case "art": if (e.ult) hold(FIGHT_CHEER.ultArt); break;
    case "clash": if (e.big) hold(FIGHT_CHEER.bigClash); else if (!e.art) hold(FIGHT_CHEER.clash); break;
    case "hit": if (e.heavy) hold(FIGHT_CHEER.heavyHit); break;
    default: break;
  }
}

const Y = 0.95;
export function pump(e, b, fx) {
  if (!fx.burst) return;   // systems not mounted yet
  switch (e.type) {
    case "enter":
      fx.ring(e.x, e.z, "#F2C14E", { r0: 3, r1: 0.3, life: 0.5 });
      fx.ring(e.x, e.z, "#ffffff", { r0: 0.2, r1: 2.6, life: 0.7 });
      fx.pillar(e.x, e.z, "#F2C14E", { life: 0.9, w: 1.4 });
      fx.burst(e.x, 0.3, e.z, "#F2C14E", 40, 5, { up: 3, life: 0.9, size: 0.5, white: 0.3 });
      break;
    case "recall":
      fx.burst(e.x, 0.8, e.z, "#B388FF", 40, 2, { rise: 5, life: 1.0, size: 0.45, spread: 1.2 });
      break;
    case "windup": {
      const f = findBeing(b, e.uid);
      if (!f) break;
      fx.ring(e.x, e.z, f.side === 0 ? "#7CE7FF" : "#FF4D8D", { r0: e.ult ? 4 : 2.2, r1: 0.4, life: Math.max(0.2, (f.move ? f.move.windup : 0.3) * (e.ult ? 1 / 0.45 : 1)) });
      if (e.ult) fx.pillar(e.x, e.z, colorFor(b, e.uid), { life: 2.2, w: 2.2, follow: () => findBeing(b, e.uid) });
      break;
    }
    case "lunge":
      fx.burst(e.x, 0.1, e.z, "#8a78aa", 12, 4, { flat: true, life: 0.5, size: 0.5, rise: 0.5 });
      strikeLook(fx, e, findBeing(b, e.uid), e.color);   // each being's own Lyt Attack (artSignatures STRIKES)
      break;
    case "dodge":
      fx.burst(e.x, 0.3, e.z, "#c9b8ff", 18, 6, { flat: true, life: 0.35, size: 0.35, white: 0.5 });
      fx.ring(e.x, e.z, "#c9b8ff", { r0: 0.3, r1: 1.4, life: 0.25 });
      break;
    case "fire":
      fx.burst(e.x, Y, e.z, e.color, 8 + e.count * 2, 4, { life: 0.3, size: 0.5, white: 0.4 });
      strikeLook(fx, e, findBeing(b, e.uid), e.color);
      break;
    case "beam":
      fx.ring(e.x, e.z, e.color, { r0: 0.4, r1: 2.6, life: 0.5, y: Y, vertical: true, yaw: (findBeing(b, e.uid) || {}).yaw || 0 });
      fx.punch(3);
      break;
    case "nova":
      fx.ring(e.x, e.z, e.color, { r0: 0.3, r1: e.r, life: 0.5 });
      fx.ring(e.x, e.z, "#ffffff", { r0: 0.2, r1: e.r * 0.8, life: 0.35 });
      fx.burst(e.x, 0.5, e.z, e.color, e.ult ? 140 : 70, e.r * 2.2, { flat: true, life: 0.8, size: 0.7, white: 0.3, up: 1.5 });
      if (e.ult) { fx.pillar(e.x, e.z, e.color, { life: 1.2, w: e.r }); fx.punch(6); }
      break;
    case "impact":
      fx.burst(e.x, 0.3, e.z, e.color, 22, 5, { up: 3, life: 0.55, size: 0.55, white: 0.35 });
      fx.ring(e.x, e.z, e.color, { r0: 0.2, r1: e.r * 1.3, life: 0.35 });
      break;
    // ── SHATTERPROOF events ──────────────────────────────────
    case "cast": {
      // the caster's hands light up; a self-Art blooms around it now,
      // an aimed one shows its warning (the Zones system draws that)
      const f = findBeing(b, e.uid); const c = colorFor(b, e.uid);
      if (f) fx.burst(f.x, 1.1, f.z, c, 18, 3, { life: 0.5, size: 0.45, rise: 2, white: 0.3 });
      if (f) fx.text(f.x, 3.1, f.z, e.name, e.ult ? "#FF4D8D" : e.kind === "tome" ? "#F2C14E" : "#ffffff", { small: !e.ult, big: e.ult, life: 1.3 });
      if (e.ult) fx.pillar(e.x, e.z, c, { life: 1.2, w: 2.4 });
      castLook(fx, e, f, c);                              // the casting circle under it while it charges
      break;
    }
    case "art": {
      artLook(fx, e, b, findBeing(b, e.uid));             // the Art's OWN look (artSignatures SHAPES / SELF / global)
      if (e.target === "area") {
        fx.ring(e.x, e.z, e.color, { r0: 0.3, r1: e.r, life: 0.45 });
        fx.ring(e.x, e.z, "#ffffff", { r0: 0.2, r1: e.r * 0.8, life: 0.3 });
        fx.burst(e.x, 0.6, e.z, e.color, e.ult ? 120 : 50, e.r * 2.4, { flat: true, life: 0.7, size: 0.6, white: 0.35, up: 1.5 });
        if (e.ult) { fx.pillar(e.x, e.z, e.color, { life: 1.1, w: e.r }); fx.punch(6); }
        else fx.punch(2);
      } else {
        const f = findBeing(b, e.uid);
        if (f) { fx.ring(f.x, f.z, e.color, { r0: 0.4, r1: 2.2, life: 0.5 }); fx.burst(f.x, 0.9, f.z, e.color, 40, 3, { rise: 3, life: 0.9, size: 0.5, spread: 1.2 }); }
        if (e.target === "global") { fx.ring(0, 0, e.color, { r0: 1, r1: 14, life: 0.9 }); fx.shake(0.6, 0.5); }
      }
      break;
    }
    case "primera": {
      const c = colorFor(b, e.uid);
      if (e.kind === "phase") { fx.ring(e.x, e.z, "#B388FF", { r0: 0.4, r1: 1.8, life: 0.35 }); fx.burst(e.x, 1, e.z, "#B388FF", 24, 2.5, { life: 0.6, size: 0.45, rise: 1.5 }); }
      if (e.kind === "mark") { fx.ring(e.tx, e.tz, c, { r0: 1.6, r1: 0.3, life: 1.0 }); fx.ring(e.tx, e.tz, "#ffffff", { r0: 0.2, r1: 1.2, life: 1.0 }); fx.burst(e.x, 1, e.z, c, 14, 2, { life: 0.5, size: 0.4, rise: 2 }); }
      if (e.kind === "arrive") { fx.pillar(e.tx, e.tz, c, { life: 0.5, w: 1.4 }); fx.burst(e.tx, 0.6, e.tz, c, 36, 5, { life: 0.5, size: 0.5, white: 0.5 }); fx.burst(e.x, 0.8, e.z, c, 24, 1.5, { rise: 4, life: 0.8, size: 0.4 }); }
      if (e.kind === "rewind") {
        // a ghost-trail from where it was to where it was a second ago
        for (let i = 0; i <= 8; i++) { const k = i / 8; fx.burst(e.x + (e.tx - e.x) * k, 0.9, e.z + (e.tz - e.z) * k, "#4ae8ff", 4, 0.8, { life: 0.6, size: 0.45, rise: 0.5, drag: 4 }); }
        fx.ring(e.tx, e.tz, "#4ae8ff", { r0: 1.8, r1: 0.4, life: 0.4 }); fx.text(e.tx, 2.6, e.tz, "REWIND", "#4ae8ff", { small: true, life: 0.7 });
      }
      if (e.kind === "leap") fx.burst(e.x, 0.1, e.z, "#8a78aa", 24, 5, { flat: true, life: 0.5, size: 0.5 });
      if (e.kind === "land") { fx.ring(e.x, e.z, c, { r0: 0.3, r1: 2.4, life: 0.4 }); fx.burst(e.x, 0.1, e.z, "#8a78aa", 30, 6, { flat: true, life: 0.6, size: 0.55 }); fx.scar("scorch", e.x, e.z, { r: 1 }); }
      break;
    }
    case "construct": {
      fx.ring(e.x, e.z, e.color, { r0: 0.2, r1: 1.6, life: 0.5 }); fx.burst(e.x, 0.4, e.z, e.color, 30, 3, { up: 3, life: 0.7, size: 0.45, white: 0.3 });
      const k = b.constructs.find((x) => x.id === e.id), a = k && ARTS[k.art];   // its name, once, as it arrives — so a Tome on the field is never a mystery
      if (a) fx.text(e.x, 2.5, e.z, a.name, "#F2C14E", { small: true, life: 1.8 });
      break;
    }
    case "construct_ruined":
      fx.burst(e.x, 0.6, e.z, e.color, 40, 5, { life: 0.7, size: 0.5, white: 0.3 }); fx.text(e.x, 2, e.z, "RUINED", "#e8453c", { small: true });
      break;
    case "resurrect":
      if (e.to === "hand") break;
      break;
    case "hit": {
      if (e.by != null && !e.tick && !e.construct) {      // a Lyt Attack's mark on the floor
        const by = findBeing(b, e.by);
        if (by && by.move && by.move.kind === "lunge") impactLook(fx, e, by, colorFor(b, e.by));
      }
      if (e.construct) { fx.burst(e.x, 0.8, e.z, e.color, 16, 5, { life: 0.4, size: 0.4, white: 0.5 }); break; }
      if (e.where) {
        // Lyf (the shield) numbers in Lyf blue; Anima (the life) in rose
        const col = e.where === "lyf" ? "#4CC9F0" : "#FF4D8D";
        fx.burst(e.x, Y, e.z, e.color, e.tick ? 6 : e.heavy ? 30 : 16, e.heavy ? 8 : 5, { life: 0.45, size: 0.5, white: 0.45 });
        fx.text(e.x, 2.3, e.z, `-${Math.round(e.dmg)}`, col, { small: !!e.tick, big: !!e.heavy && e.where === "anima" });
        if (e.heavy && !e.tick) fx.punch(2.5);
        break;
      }
      if (e.tick) {
        fx.burst(e.x, Y, e.z, e.color, 4, 5, { life: 0.3, size: 0.4, white: 0.5 });
        break;
      }
      fx.burst(e.x, Y, e.z, e.color, e.heavy ? 34 : 16, e.heavy ? 9 : 6, { life: 0.45, size: e.heavy ? 0.6 : 0.45, white: 0.45 });
      if (e.heavy) {
        fx.ring(e.x, e.z, "#ffffff", { r0: 0.3, r1: 1.8, life: 0.25, y: Y, vertical: true, yaw: Math.random() * 3 });   // eslint-disable-line -- visual only
        fx.punch(2.5);
      }
      fx.text(e.x, 2.3, e.z, e.crit ? `CRIT ${e.dmg}` : String(e.dmg), e.crit ? "#ff4d8d" : "#ffffff", { big: e.crit, small: !e.heavy && !e.crit });
      break;
    }
    case "clash":
      if (e.art) {
        // Arts meeting Arts: a quick bright burst, no banner word
        fx.burst(e.x, Y, e.z, e.color || "#fff", 14, 6, { life: 0.4, size: 0.5, white: 0.5 });
        fx.burst(e.x, Y, e.z, e.color2 || "#fff", 10, 5, { life: 0.4, size: 0.45, white: 0.4 });
        fx.ring(e.x, e.z, "#ffffff", { r0: 0.2, r1: 1.2, life: 0.22, y: Y, vertical: true, yaw: Math.atan2(e.x, e.z) });
        break;
      }
      fx.text(e.x, 2.6, e.z, e.beam ? "BEAM CLASH!" : "CLASH!", "#F2C14E", { big: true, scale: e.big ? 1.2 : 0.75 });
      fx.burst(e.x, Y, e.z, e.color || "#fff", e.big ? 60 : 18, e.big ? 12 : 6, { life: 0.6, size: 0.6, white: 0.5 });
      fx.burst(e.x, Y, e.z, e.color2 || "#fff", e.big ? 60 : 18, e.big ? 12 : 6, { life: 0.6, size: 0.6, white: 0.5 });
      fx.ring(e.x, e.z, "#ffffff", { r0: 0.3, r1: e.big ? 4 : 1.6, life: 0.4 });
      if (e.big) { fx.ring(e.x, e.z, "#F2C14E", { r0: 0.3, r1: 2.6, life: 0.45, y: Y, vertical: true, yaw: Math.atan2(e.x, e.z) }); fx.punch(5); }
      break;
    case "text":
      fx.text(e.x, 2.7, e.z, e.text, e.color, { big: e.big, small: e.small });
      break;
    case "shake":
      fx.shake(e.amp, e.dur);
      break;
    case "scorch":
      if (e.rune) { fx.scar("rune", e.x, e.z, { r: e.r * 0.75, color: e.color }); fx.scar("crater", e.x, e.z, { r: e.r * 0.6, color: e.color }); }
      else if (e.crater) fx.scar("crater", e.x, e.z, { r: e.r, color: e.color });
      else if (e.streak) fx.scar("groove", e.x, e.z, { len: e.r * 2, r: 1, yaw: e.yaw, color: e.color });
      else fx.scar("scorch", e.x, e.z, { r: e.r, color: e.color });
      break;
    case "status": {
      const s = STATUS[e.status];
      if (s) fx.text(e.x, 2.9, e.z, s.label, s.color, { small: true, life: 1.1 });
      break;
    }
    case "heal":
      fx.text(e.x, 2.5, e.z, `+${e.amt}${e.anima ? " ANIMA" : ""}`, e.anima ? "#FF4D8D" : "#9cffb0", {});
      fx.burst(e.x, 0.4, e.z, "#9cffb0", 26, 1.2, { rise: 3, life: 1.0, size: 0.4, spread: 1.4, drag: 1 });
      break;
    case "ward":
      fx.ring(e.x, e.z, "#7fe8d8", { r0: 0.6, r1: 1.6, life: 0.35, y: Y, vertical: true, yaw: Math.atan2(e.x, e.z) });
      break;
    case "ruined":
      fx.text(e.x, 2.8, e.z, "RUINED", "#e8453c", { big: true, life: 1.6 });
      fx.burst(e.x, 0.8, e.z, colorFor(b, e.uid), 90, 3, { rise: 4, life: 2.0, size: 0.55, spread: 1.4, drag: 0.8 });
      fx.ring(e.x, e.z, "#e8453c", { r0: 0.4, r1: 3.5, life: 0.8 });
      fx.scar("crater", e.x, e.z, { r: 1.6, color: "#e8453c" });
      break;
    case "transcend_start":
      fx.pillar(e.x, e.z, colorFor(b, e.uid), { life: 2.7, w: 2.6, follow: () => findBeing(b, e.uid) });
      fx.ring(e.x, e.z, colorFor(b, e.uid), { r0: 5, r1: 0.5, life: 1.2 });
      break;
    case "transcend":
      fx.burst(e.x, 1.2, e.z, colorFor(b, e.uid), 160, 12, { life: 1.0, size: 0.7, white: 0.4, up: 2 });
      fx.burst(e.x, 1.2, e.z, "#F2C14E", 80, 7, { life: 1.4, size: 0.5, rise: 3 });
      fx.ring(e.x, e.z, "#ffffff", { r0: 0.4, r1: 6, life: 0.7 });
      fx.ring(e.x, e.z, colorFor(b, e.uid), { r0: 0.4, r1: 4, life: 0.9, y: 1.2, vertical: true, yaw: Math.atan2(e.x, e.z) });
      fx.text(e.x, 2.9, e.z, TIER_LABEL[e.to].toUpperCase() + "!", "#F2C14E", { big: true, life: 1.4 });
      fx.punch(8);
      break;
    case "anima":
      if (e.x != null && e.delta < 0) fx.text(e.x, 3.3, e.z, `ANIMA ${e.delta}`, "#FF4D8D", { small: !e.self, life: 1.2 });
      break;
    case "overtime":
      fx.ring(0, 0, "#FF4D8D", { r0: 14, r1: 0.5, life: 2.4 });
      break;
    default: break;
  }
}

function findBeing(b, uid) {
  for (const s of b.sides) for (const r of s.roster) if (r.uid === uid) return r;
  return null;
}
const COLORS = {
  spectral_tiger: "#7fe8d8", chaos_dragon: "#ff9b2e", z4z: "#4ae8ff", xulan: "#c9b8ff",
  shadow_fox: "#ffc46a", inverted_serpent: "#ff8aa0", antdroid: "#ffb02e", dg0: "#7fe8d8",
  h01: "#4ac8ff", h09: "#ffb04a", lucky_dragon: "#ffb02e", premier_dragon: "#ffd24a",
};
const colorFor = (b, uid) => { const f = findBeing(b, uid); return f ? COLORS[f.line] || "#fff" : "#fff"; };

// ── FREE AIM: the cursor, cast onto the field, every frame ──────
const PLANE = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const HIT = new THREE.Vector3();
function AimProbe({ battle, mySide, spectate }) {
  const ring = useRef(), line = useRef();
  const lineGeo = useMemo(() => { const g = new THREE.BufferGeometry(); g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(6), 3)); return g; }, []);
  useFrame(({ raycaster, pointer, camera, clock }) => {
    const b = battle.current; if (!b) return;
    if (spectate) { if (ring.current) ring.current.visible = false; if (line.current) line.current.visible = false; return; }
    raycaster.setFromCamera(pointer, camera);
    if (!raycaster.ray.intersectPlane(PLANE, HIT)) return;
    const r = Math.hypot(HIT.x, HIT.z), R = ARENA_R + 1;
    if (r > R) { HIT.x *= R / r; HIT.z *= R / r; }
    issue(b, mySide, { type: "aim", x: HIT.x, z: HIT.z });
    const sd = b.sides[mySide], f = sd.active >= 0 ? sd.roster[sd.active] : null;
    if (ring.current) { ring.current.visible = true; ring.current.position.set(HIT.x, 0.05, HIT.z); ring.current.rotation.z = clock.elapsedTime * 1.5; }
    if (line.current) {
      line.current.visible = !!f;
      if (f) { const a = lineGeo.attributes.position.array; a[0] = f.x; a[1] = 0.06; a[2] = f.z; a[3] = HIT.x; a[4] = 0.06; a[5] = HIT.z; lineGeo.attributes.position.needsUpdate = true; lineGeo.computeBoundingSphere(); }
    }
  });
  return (
    <>
      <mesh ref={ring} rotation={[-Math.PI / 2, 0, 0]} renderOrder={4}>
        <ringGeometry args={[0.32, 0.42, 4]} />
        <meshBasicMaterial color="#F2C14E" transparent opacity={0.9} depthWrite={false} toneMapped={false} />
      </mesh>
      <line ref={line} geometry={lineGeo} renderOrder={4}>
        <lineBasicMaterial color="#F2C14E" transparent opacity={0.35} depthWrite={false} />
      </line>
    </>
  );
}

function BattleSceneImpl({ battle, fx, paused, userCam, mySide = 0, spectate = false, arena = "shard" }) {
  const cue = useRef({}), director = useRef({ names: null, teams: null });   // the crowd's ears (Stands reads both)
  const wide = CROWD_IN_FIGHT;
  if (typeof window !== "undefined") window.__battleCue = cue.current;   // console handle (like __ringsideAtlas): cheer / roar / waveReq
  return (
    <Canvas
      dpr={[1, 1.5]}
      gl={{ antialias: true, powerPreference: "high-performance" }}
      camera={{ fov: 48, near: 0.1, far: wide ? 3.4 * WORLD_BOUNDS.radius * 1.6 : 140, position: [0, 6, 14] }}
      onCreated={({ gl }) => { gl.toneMapping = THREE.ACESFilmicToneMapping; gl.toneMappingExposure = 1.05; }}
    >
      <color attach="background" args={["#07040d"]} />
      <fog attach="fog" args={wide ? ["#120a22", 110, 300] : ["#120a22", 26, 60]} />
      <Lights />
      <Arena bounds={wide ? WORLD_BOUNDS : undefined} variant={arena} />
      {wide && <Stands cue={cue} director={director} teams={[]} />}
      {/* THE SKY BOX (spectators): the suite, you and your friends in it (it mounts its own fail-safe watch) */}
      {wide && spectate && <SkyBox userCam={userCam} />}
      <Fighter battle={battle} side={0} fx={fx} />
      <Fighter battle={battle} side={1} fx={fx} />
      <Effects battle={battle} fx={fx} />
      <ArtFx fx={fx} />
      <BattleCamera battle={battle} fx={fx} userCam={userCam} mySide={mySide} cue={cue} />
      <AimProbe battle={battle} mySide={mySide} spectate={spectate} />
      <Clock battle={battle} fx={fx} paused={paused} cue={cue} director={director} />
    </Canvas>
  );
}

// props are refs and never change during a fight — the memo always bails
export default memo(BattleSceneImpl);
